<script lang="ts">
  import { MOD_SLOTS, modsForSlot, WEAPON_MODS, type ModSlot } from '../data/weapon-mods.js';
  import { i18next } from '../i18n.js';
  import { lang as langState } from '../lang-state.svelte.js';

  interface BuiltInMod {
    slot: string;
    stat?: string | null;
    value?: number;
    name_ru?: string;
    name_en?: string;
  }

  interface Props {
    mods: Record<ModSlot, string | null>;
    onPick: (slot: ModSlot, id: string | null) => void;
    available: ModSlot[];
    weaponCategory?: string;
    builtInMods?: BuiltInMod[];
    isExotic?: boolean;
  }

  let { mods, onPick, available, weaponCategory, builtInMods = [], isExotic = false }: Props = $props();
  let lang = $derived(langState.current);

  function builtInForSlot(slot: ModSlot): BuiltInMod | null {
    // map magazine slot from data sometimes uses 'magazine' or 'mag'
    return builtInMods.find((m) => m.slot === slot || (slot === 'optic' && m.slot === 'scope')) || null;
  }

  /** When exotic weapon has a user-picked mod but no built-in data for that slot,
   *  resolve the picked mod into the same shape so we render a unified orange-bold tile. */
  function pickedAsBuiltIn(slot: ModSlot): BuiltInMod | null {
    const id = mods[slot];
    if (!id) return null;
    const m = WEAPON_MODS.find((x) => x.id === id);
    if (!m) return null;
    const name = i18next.t(m.id, { ns: 'weapon-mods', defaultValue: m.id }) as string;
    return {
      slot,
      stat: (m as { stat?: string | null }).stat ?? null,
      value: (m as { value?: number }).value ?? 0,
      name_en: name,
      name_ru: name,
    };
  }

  function modsFiltered(slot: ModSlot) {
    const all = WEAPON_MODS.filter((m) => m.slot === slot);
    if (!weaponCategory) return all;
    return all.filter((m) => {
      const cls = (m as { weaponClasses?: string[] }).weaponClasses;
      if (!cls || cls.length === 0) return true;
      return cls.includes(weaponCategory);
    });
  }

  const SLOT_LABELS: Record<ModSlot, { icon: string; ruLabel: string; enLabel: string }> = {
    optic: { icon: '🔭', ruLabel: 'Прицел', enLabel: 'Optic' },
    muzzle: { icon: '⬛', ruLabel: 'Дульная', enLabel: 'Muzzle' },
    underbarrel: { icon: '▐', ruLabel: 'Подствол', enLabel: 'Underbarrel' },
    magazine: { icon: '🔫', ruLabel: 'Магазин', enLabel: 'Magazine' },
  };

  let slotsAvailable = $derived(new Set(available));

  function label(slot: ModSlot): string {
    return langState.current === 'en' ? SLOT_LABELS[slot].enLabel : SLOT_LABELS[slot].ruLabel;
  }
</script>

<section class="panel wm">
  <div class="panel-title"><span>{langState.current === 'ru' ? 'Моды оружия' : 'Weapon Mods'}</span></div>

  {#if isExotic}
    <div class="exotic-info" title={lang === 'ru' ? 'Экзотические оружия чаще всего имеют встроенные моды и не принимают пользовательские; калькулятор уже учитывает intrinsic-атрибуты' : 'Exotic weapons usually have built-in mods and ignore user-picked ones; the calculator already accounts for intrinsic attributes'}>
      ℹ️ {lang === 'ru'
        ? 'Экзотик: моды обычно встроенные (built-in) или пассивные. Пользовательские моды могут не применяться в игре. Intrinsic-атрибуты учитываются автоматически.'
        : 'Exotic: mods are typically built-in or passive. User-picked mods may not apply in-game. Intrinsic attributes are auto-applied by the calculator.'}
    </div>
  {/if}

  {#each MOD_SLOTS as slot (slot)}
    {@const modList = modsFiltered(slot)}
    {@const builtin = builtInForSlot(slot)}
    {@const picked = !builtin && isExotic ? pickedAsBuiltIn(slot) : null}
    {@const tile = builtin ?? picked}
    <div class="row" class:na={!slotsAvailable.has(slot) && !tile} class:locked={!!tile}>
      <div class="slot-lbl">
        <span class="ico">{SLOT_LABELS[slot].icon}</span>
        <span>{label(slot)}</span>
        {#if tile}<span class="lock" title={builtin ? (lang === 'ru' ? 'Встроенный мод экзотика — применяется автоматически' : 'Exotic built-in mod — auto-applied') : (lang === 'ru' ? 'Мод выбран; на экзотике обычно проходит как пассивный' : 'Mod picked; usually treated as passive on exotics')}>🔒</span>{/if}
      </div>
      {#if tile}
        <div class="builtin">
          <span class="bn">{lang === 'ru' ? (tile.name_ru || tile.name_en || '—') : (tile.name_en || tile.name_ru || '—')}</span>
          {#if tile.stat && tile.value}
            {@const isFlat = tile.stat === 'mag_add'}
            {@const statName = isFlat ? (lang === 'ru' ? 'патронов' : 'rounds') : (i18next.t(tile.stat, { ns: 'stats', defaultValue: tile.stat }) as string)}
            <span class="bv">+{tile.value}{isFlat ? '' : '%'} {statName}</span>
          {/if}
          {#if !builtin && picked}
            <button class="tile-clear" title={lang === 'ru' ? 'Снять мод' : 'Unpick mod'} onclick={() => onPick(slot, null)}>×</button>
          {/if}
        </div>
      {:else}
        <select
          class="input"
          class:exotic-empty={isExotic}
          value={mods[slot] ?? ''}
          disabled={!slotsAvailable.has(slot)}
          onchange={(e) => onPick(slot, (e.currentTarget as HTMLSelectElement).value || null)}
        >
          <option value="">— —</option>
          {#each modList as m (m.id)}
            {@const modName = i18next.t(m.id, { ns: 'weapon-mods', defaultValue: m.id }) as string}
            <option value={m.id}>{modName}{m.stat && m.value ? ` (${m.value > 0 ? '+' : ''}${m.value}% ${m.stat.toUpperCase()})` : ''}</option>
          {/each}
        </select>
      {/if}
    </div>
  {/each}
</section>

<style>
  .wm { margin-top: 8px; }
  .row { display: grid; grid-template-columns: 90px 1fr; gap: 6px; align-items: center; margin-bottom: 4px; }
  .row.na { opacity: .4; }
  .slot-lbl { display: flex; align-items: center; gap: 4px; font: 700 10px/1 var(--f-display); letter-spacing: .1em; text-transform: uppercase; color: var(--orange); }
  .ico { font-size: 11px; }
  .input { padding: 5px 8px; font-size: 11px; }
  .row.locked .slot-lbl { color: var(--orange); }
  .lock { font-size: 9px; opacity: .7; margin-left: 2px; }
  .builtin {
    display: flex; justify-content: space-between; align-items: center; gap: 6px;
    padding: 5px 8px; font-size: 11px;
    background: rgba(255, 165, 0, 0.05);
    border: 1px dashed rgba(255, 165, 0, 0.3);
    border-radius: 3px;
  }
  .bn { color: var(--orange); font-weight: 600; }
  .bv { color: var(--orange); font: 700 10px/1 var(--f-display); letter-spacing: .05em; }
  .tile-clear {
    background: transparent; border: none; color: var(--muted);
    cursor: pointer; font: 700 14px/1 var(--f-display); padding: 0 4px; margin-left: 4px;
  }
  .tile-clear:hover { color: var(--red); }
  /* Empty mod slot dropdown on exotic — orange border so it visually matches built-in tiles */
  .input.exotic-empty {
    border-color: rgba(255,165,0,.3);
    background: rgba(255,165,0,.04);
    color: var(--orange);
  }
  .exotic-info {
    margin: 0 0 8px;
    padding: 6px 10px;
    font-size: 11px; line-height: 1.4;
    color: var(--exotic);
    background: rgba(255,107,61,.08);
    border: 1px solid rgba(255,107,61,.3);
    border-left: 3px solid var(--exotic);
    border-radius: 3px;
  }
</style>
